import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    PhoneOutgoing, SlidersHorizontal, Users, Loader2, Check,
    CheckCircle2, AlertTriangle, Info, RefreshCw, Package, ChevronRight, ChevronDown, Plus, X, Download,
} from 'lucide-react'
import {
    cuGetDispositionTree, cuGetProductTree, cuListLeadLists, cuListCampaigns,
    cuCountLeads, cuPushLeads, cuPushJobStatus, cuActivePushJobs, isJobActive, cuExportLeads, cuCreateLeadList, emptyFilters,
    PREVIEW_CAMPAIGNS, PREVIEW_LEAD_LISTS, EXPORT_MAX_ROWS,
    AGEING_BUCKETS, dispositionBucketFor,
    CampaignFilters, CampaignBucket, DispositionTree, DispositionOption, ProductCategory,
    DialerCampaign, LeadList, DuplicateOption, PushResult, PushJob,
} from '@/actions/campaign-upload'
import { showToast } from '@/components/ui/Toast'
import { downloadFile } from '@/services/exportService'

const BRAND = import.meta.env.VITE_BRAND_COLOR || '#153d23'
const LARGE_PUSH_THRESHOLD = 5000

const DATE_LABELS: Partial<Record<CampaignBucket, string>> = {
    abandoned_cart: 'Last ABC Date',
    reorder: 'Reorder Due Date',
    rto: 'RTO Date',
}

const BUCKET_OPTIONS: { value: CampaignBucket; label: string }[] = [
    { value: 'abandoned_cart', label: 'Abandoned Cart' },
    { value: 'reorder', label: 'Reorder' },
    { value: 'rto', label: 'RTO' },
    { value: 'edd', label: 'EDD' },
    { value: 'ndr', label: 'NDR / Failed Delivery' },
]

/** Which screen an ageing bucket mirrors, shown so the count is not a surprise. */
const AGEING_HINTS: Partial<Record<CampaignBucket, string>> = {
    edd: 'Undelivered orders with no failed delivery attempt — same set as the EDD screen.',
    ndr: 'Orders where a delivery attempt genuinely failed — same set as the Failed Delivery screen.',
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-offset-0'
const smallInputCls = 'w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none'
const sectionLabel = 'text-xs font-semibold text-gray-500 uppercase tracking-wide'

/** min/max day-count range, used for the EDD/NDR ageing filters. */
const DayRange: React.FC<{
    label: string
    hint?: string
    min: number | null
    max: number | null
    onChange: (bound: 'min' | 'max', v: number | null) => void
}> = ({ label, hint, min, max, onChange }) => {
    const parse = (raw: string): number | null => {
        const t = raw.trim()
        if (t === '') return null
        const n = Number(t)
        return Number.isFinite(n) ? Math.trunc(n) : null
    }
    const invalid = min != null && max != null && min > max
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-gray-600">{label}</label>
                {(min != null || max != null) && (
                    <button onClick={() => { onChange('min', null); onChange('max', null) }}
                        className="text-[11px] text-gray-400 hover:text-gray-600">Clear</button>
                )}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <input type="number" inputMode="numeric" placeholder="Min"
                    value={min ?? ''} onChange={e => onChange('min', parse(e.target.value))}
                    className={`${smallInputCls} ${invalid ? 'border-red-300' : ''}`} />
                <input type="number" inputMode="numeric" placeholder="Max"
                    value={max ?? ''} onChange={e => onChange('max', parse(e.target.value))}
                    className={`${smallInputCls} ${invalid ? 'border-red-300' : ''}`} />
            </div>
            {invalid
                ? <p className="text-[10px] text-red-500 mt-1">Min is greater than Max — no leads can match.</p>
                : hint && <p className="text-[10px] text-gray-400 mt-1">{hint}</p>}
        </div>
    )
}

const DUP_OPTIONS: { value: DuplicateOption; label: string; hint: string }[] = [
    { value: 'skip', label: 'Skip', hint: 'Ignore existing' },
    { value: 'overwrite', label: 'Overwrite', hint: 'Update matches' },
    { value: 'clone', label: 'Clone', hint: 'Add as new' },
]

/** A single disposition level: checkbox multi-select dropdown with search. */
const DispLevel: React.FC<{
    label: string
    options: DispositionOption[]
    selected: number[]
    onToggle: (id: number) => void
    onSelectAll: (ids: number[], select: boolean) => void
    onClear: () => void
    disabled?: boolean
    empty: string
}> = ({ label, options, selected, onToggle, onSelectAll, onClear, disabled, empty }) => {
    const [open, setOpen] = useState(false)
    const [q, setQ] = useState('')
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
        document.addEventListener('mousedown', h)
        return () => document.removeEventListener('mousedown', h)
    }, [])

    const isDisabled = disabled || options.length === 0
    const filtered = q.trim()
        ? options.filter(o => o.value.toLowerCase().includes(q.trim().toLowerCase()))
        : options
    const summary = selected.length === 0
        ? 'All'
        : selected.length === 1
            ? (options.find(o => o.option_id === selected[0])?.value ?? '1 selected')
            : `${selected.length} selected`

    return (
        <div ref={ref} className="relative">
            <label className="block text-xs text-gray-600 mb-1">{label}</label>
            <button type="button" disabled={isDisabled} onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2"
                style={{ ['--tw-ring-color' as any]: BRAND }}>
                <span className={`truncate ${selected.length ? 'text-gray-800' : 'text-gray-400'}`}>
                    {isDisabled ? empty : summary}
                </span>
                <ChevronDown size={15} className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && !isDisabled && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                        <input autoFocus value={q} onChange={e => setQ(e.target.value)}
                            placeholder="Search…"
                            className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1"
                            style={{ ['--tw-ring-color' as any]: BRAND }} />
                    </div>
                    <div className="max-h-52 overflow-y-auto divide-y divide-gray-50">
                        {filtered.length > 0 && (() => {
                            const ids = filtered.map(o => o.option_id)
                            const allOn = ids.every(id => selected.includes(id))
                            return (
                                <button onClick={() => onSelectAll(ids, !allOn)}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 bg-gray-50/40">
                                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${allOn ? 'border-transparent text-white' : 'border-gray-300'}`}
                                        style={allOn ? { backgroundColor: BRAND } : undefined}>
                                        {allOn && <Check size={11} />}
                                    </span>
                                    <span className="text-xs font-medium text-gray-700">
                                        {allOn ? 'Deselect all' : 'Select all'}{q.trim() ? ' matching' : ''} ({ids.length})
                                    </span>
                                </button>
                            )
                        })()}
                        {filtered.length === 0 ? (
                            <div className="px-3 py-3 text-xs text-gray-400 text-center">No matches</div>
                        ) : filtered.map(o => {
                            const active = selected.includes(o.option_id)
                            return (
                                <button key={o.option_id} onClick={() => onToggle(o.option_id)}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50">
                                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${active ? 'border-transparent text-white' : 'border-gray-300'}`}
                                        style={active ? { backgroundColor: BRAND } : undefined}>
                                        {active && <Check size={11} />}
                                    </span>
                                    <span className="text-xs text-gray-800 truncate">{o.value}</span>
                                </button>
                            )
                        })}
                    </div>
                    {selected.length > 0 && (
                        <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-100 bg-gray-50">
                            <span className="text-[11px] text-gray-500">{selected.length} selected</span>
                            <button onClick={onClear} className="text-[11px] text-gray-500 hover:text-gray-700">Clear</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

const CampaignUpload: React.FC = () => {
    // ── Filter state ──────────────────────────────────────────────────────────
    const [filters, setFilters] = useState<CampaignFilters>(emptyFilters())
    const setF = <K extends keyof CampaignFilters>(k: K, v: CampaignFilters[K]) =>
        setFilters(prev => ({ ...prev, [k]: v }))

    // ── Reference data ──────────────────────────────────────────────────────
    const [tree, setTree] = useState<DispositionTree | null>(null)
    const [treeLoading, setTreeLoading] = useState(false)
    const [productTree, setProductTree] = useState<ProductCategory[]>([])
    const [productQuery, setProductQuery] = useState('')
    const [expandedCats, setExpandedCats] = useState<Set<number>>(new Set())
    const [leadLists, setLeadLists] = useState<LeadList[]>([])
    const [campaigns, setCampaigns] = useState<DialerCampaign[]>([])
    const [tataPreview, setTataPreview] = useState(false)

    // ── Destination + push ────────────────────────────────────────────────────
    const [leadListId, setLeadListId] = useState<number | null>(null)
    const [campaignId, setCampaignId] = useState<number | null>(null)
    const [dupOption, setDupOption] = useState<DuplicateOption>('skip')
    const [clearExisting, setClearExisting] = useState(false)
    const [count, setCount] = useState<number | null>(null)
    const [countLoading, setCountLoading] = useState(false)
    const [countUnavailable, setCountUnavailable] = useState(false)
    const [isPushing, setIsPushing] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [result, setResult] = useState<PushResult | null>(null)
    // The queued push being worked off in the background (polled until it finishes).
    const [job, setJob] = useState<PushJob | null>(null)
    const jobActive = isJobActive(job)

    // ── Create-list modal ──────────────────────────────────────────────────────
    const [createOpen, setCreateOpen] = useState(false)
    const [newName, setNewName] = useState('')
    const [newDesc, setNewDesc] = useState('')
    const [creating, setCreating] = useState(false)
    const nameValid = /^[A-Za-z0-9 ]{1,25}$/.test(newName.trim())

    const isAgeingBucket = !!filters.bucket && AGEING_BUCKETS.includes(filters.bucket)

    // ── Load disposition tree when bucket changes ─────────────────────────────
    // EDD and NDR share the 'edd' tree (same lead_bucket, split only by whether a
    // delivery attempt failed), so switching between them must not refetch or wipe
    // the disposition selection.
    const dispBucket = dispositionBucketFor(filters.bucket)
    useEffect(() => {
        setTreeLoading(true)
        setFilters(prev => ({
            ...prev, l1: [], l2: [], l3: [],
            date_from: null, date_to: null,
            dispatch_ageing_min: null, dispatch_ageing_max: null,
            days_overdue_min: null, days_overdue_max: null,
        }))
        let cancelled = false
        cuGetDispositionTree(dispBucket)
            .then(res => { if (!cancelled) setTree((res?.data as DispositionTree) ?? null) })
            .catch(() => { if (!cancelled) setTree(null) })
            .finally(() => { if (!cancelled) setTreeLoading(false) })
        return () => { cancelled = true }
        // Keyed on the tree, not the bucket: switching EDD <-> NDR keeps the same
        // disposition tree, so the selection stays valid and is deliberately preserved
        // (as are the ageing bounds, which mean the same thing on both screens).
    }, [dispBucket])

    // ── Load lead lists (reused after creating a new one) ─────────────────────
    const loadLeadLists = useCallback(async (): Promise<LeadList[]> => {
        try {
            const res = await cuListLeadLists()
            const lists = (res?.data as LeadList[]) ?? []
            if (lists.length) {
                setLeadLists(lists)
                setTataPreview(false)
                return lists
            }
        } catch { /* fall through to preview */ }
        setLeadLists(PREVIEW_LEAD_LISTS)
        setTataPreview(true)
        return []
    }, [])

    // ── Load products + Tata reference data once ──────────────────────────────
    useEffect(() => {
        cuGetProductTree()
            .then(res => setProductTree((res?.data as ProductCategory[]) ?? []))
            .catch(() => setProductTree([]))

        loadLeadLists()

        cuListCampaigns()
            .then(res => {
                const camps = (res?.data as DialerCampaign[]) ?? []
                if (camps.length) setCampaigns(camps)
                else { setCampaigns(PREVIEW_CAMPAIGNS); setTataPreview(true) }
            })
            .catch(() => { setCampaigns(PREVIEW_CAMPAIGNS); setTataPreview(true) })
    }, [loadLeadLists])

    const handleCreateList = async () => {
        const name = newName.trim()
        if (!nameValid || creating) return
        setCreating(true)
        try {
            const res = await cuCreateLeadList(name, newDesc.trim() || undefined)
            if (res?.success) {
                showToast('Lead list created', 'success')
                const lists = await loadLeadLists()
                const created = lists.find(l => l.name === name)
                if (created) setLeadListId(created.list_id)
                setCreateOpen(false)
                setNewName(''); setNewDesc('')
            } else {
                showToast(res?.message || 'Failed to create list', 'error')
            }
        } catch (e: any) {
            showToast(e?.message || 'Could not create the lead list', 'error')
        } finally {
            setCreating(false)
        }
    }

    // ── Debounced match count ─────────────────────────────────────────────────
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setCountLoading(true)
            setCountUnavailable(false)
            cuCountLeads(filters)
                .then(res => {
                    const d = res?.data as { count?: number } | undefined
                    if (res?.success && d && typeof d.count === 'number') {
                        setCount(d.count)
                    } else {
                        setCount(null); setCountUnavailable(true)
                    }
                })
                .catch(() => { setCount(null); setCountUnavailable(true) })
                .finally(() => setCountLoading(false))
        }, 450)
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    }, [filters])

    // ── Derived: cascading multi-select disposition options ───────────────────
    const dedupe = (opts: DispositionOption[]) => {
        const m = new Map<number, DispositionOption>()
        opts.forEach(o => m.set(o.option_id, o))
        return [...m.values()]
    }
    const l2Options = useMemo(() => {
        if (!tree) return []
        return dedupe(filters.l1.flatMap(id => tree.l2ByL1[String(id)] || []))
    }, [tree, filters.l1])
    const l3Options = useMemo(() => {
        if (!tree) return []
        return dedupe(filters.l2.flatMap(id => tree.l3ByL2[String(id)] || []))
    }, [tree, filters.l2])

    // Toggle a disposition at a level, pruning now-invalid deeper selections.
    const toggleL1 = (id: number) => setFilters(prev => {
        const l1 = prev.l1.includes(id) ? prev.l1.filter(x => x !== id) : [...prev.l1, id]
        const validL2 = new Set<number>()
        l1.forEach(x => (tree?.l2ByL1[String(x)] || []).forEach(o => validL2.add(o.option_id)))
        const l2 = prev.l2.filter(x => validL2.has(x))
        const validL3 = new Set<number>()
        l2.forEach(x => (tree?.l3ByL2[String(x)] || []).forEach(o => validL3.add(o.option_id)))
        const l3 = prev.l3.filter(x => validL3.has(x))
        return { ...prev, l1, l2, l3 }
    })
    const toggleL2 = (id: number) => setFilters(prev => {
        const l2 = prev.l2.includes(id) ? prev.l2.filter(x => x !== id) : [...prev.l2, id]
        const validL3 = new Set<number>()
        l2.forEach(x => (tree?.l3ByL2[String(x)] || []).forEach(o => validL3.add(o.option_id)))
        const l3 = prev.l3.filter(x => validL3.has(x))
        return { ...prev, l2, l3 }
    })
    const toggleL3 = (id: number) => setFilters(prev => ({
        ...prev,
        l3: prev.l3.includes(id) ? prev.l3.filter(x => x !== id) : [...prev.l3, id],
    }))

    // Bulk select/deselect a set of ids at a level (used by "Select all"), pruning deeper.
    const bulkL1 = (ids: number[], select: boolean) => setFilters(prev => {
        const set = new Set(prev.l1); ids.forEach(id => select ? set.add(id) : set.delete(id))
        const l1 = [...set]
        const validL2 = new Set<number>(); l1.forEach(x => (tree?.l2ByL1[String(x)] || []).forEach(o => validL2.add(o.option_id)))
        const l2 = prev.l2.filter(x => validL2.has(x))
        const validL3 = new Set<number>(); l2.forEach(x => (tree?.l3ByL2[String(x)] || []).forEach(o => validL3.add(o.option_id)))
        const l3 = prev.l3.filter(x => validL3.has(x))
        return { ...prev, l1, l2, l3 }
    })
    const bulkL2 = (ids: number[], select: boolean) => setFilters(prev => {
        const set = new Set(prev.l2); ids.forEach(id => select ? set.add(id) : set.delete(id))
        const l2 = [...set]
        const validL3 = new Set<number>(); l2.forEach(x => (tree?.l3ByL2[String(x)] || []).forEach(o => validL3.add(o.option_id)))
        const l3 = prev.l3.filter(x => validL3.has(x))
        return { ...prev, l2, l3 }
    })
    const bulkL3 = (ids: number[], select: boolean) => setFilters(prev => {
        const set = new Set(prev.l3); ids.forEach(id => select ? set.add(id) : set.delete(id))
        return { ...prev, l3: [...set] }
    })

    // ── Derived: product category tree (search-filtered) ──────────────────────
    const filteredCats = useMemo(() => {
        const q = productQuery.trim().toLowerCase()
        if (!q) return productTree
        return productTree
            .map(c => ({ ...c, products: c.products.filter(p => p.name.toLowerCase().includes(q)) }))
            .filter(c => c.products.length > 0)
    }, [productTree, productQuery])

    const toggleCat = (catId: number) => setExpandedCats(prev => {
        const n = new Set(prev); n.has(catId) ? n.delete(catId) : n.add(catId); return n
    })
    const toggleProduct = (id: number) =>
        setFilters(prev => ({
            ...prev,
            product_ids: prev.product_ids.includes(id)
                ? prev.product_ids.filter(x => x !== id)
                : [...prev.product_ids, id],
        }))
    // Select/deselect all products in a category
    const toggleCatProducts = (cat: ProductCategory) => setFilters(prev => {
        const ids = cat.products.map(p => p.product_id)
        const allOn = ids.every(id => prev.product_ids.includes(id))
        const set = new Set(prev.product_ids)
        ids.forEach(id => allOn ? set.delete(id) : set.add(id))
        return { ...prev, product_ids: [...set] }
    })

    const canPush = leadListId != null && !isPushing && !jobActive && (count == null || count > 0) && !tataPreview
    const isLargePush = typeof count === 'number' && count > LARGE_PUSH_THRESHOLD
    // Export doesn't touch Smartflo, so it stays available even in tataPreview mode —
    // only gated by the count being known, non-zero, and under the size cap.
    const canExport = typeof count === 'number' && count > 0 && count <= EXPORT_MAX_ROWS && !isExporting
    const exportTooLarge = typeof count === 'number' && count > EXPORT_MAX_ROWS

    const resetAll = () => {
        setFilters(emptyFilters())
        setProductQuery('')
    }

    // Rejoin a push that is still queued/running (page reload, or a teammate started it).
    useEffect(() => {
        cuActivePushJobs().then(res => {
            const first = (res?.data as PushJob[] | undefined)?.[0]
            if (res?.success && first) setJob(first)
        }).catch(() => { /* nothing to resume */ })
    }, [])

    // Poll the job until it finishes, then show the usual result card.
    useEffect(() => {
        if (!job || !isJobActive(job)) return
        const id = job.job_id
        const timer = window.setInterval(async () => {
            try {
                const res = await cuPushJobStatus(id)
                const j = res?.data as PushJob | undefined
                if (!res?.success || !j) return
                if (isJobActive(j)) { setJob(j); return }
                window.clearInterval(timer)
                setJob(null)
                setResult({
                    pushed: j.pushed, skipped: 0, failed: j.failed, cleared: j.cleared,
                    message: j.message || '', errors: j.errors,
                })
                showToast(
                    j.status === 'done' ? `Pushed ${j.pushed.toLocaleString()} lead(s) to the campaign list`
                        : j.status === 'partial' ? `Pushed ${j.pushed.toLocaleString()}, failed ${j.failed.toLocaleString()} - see details`
                        : (j.message || 'Upload failed'),
                    j.status === 'done' ? 'success' : 'error',
                )
            } catch { /* transient network error: try again on the next tick */ }
        }, 3000)
        return () => window.clearInterval(timer)
    }, [job?.job_id, job?.status])

    const doPush = async () => {
        if (leadListId == null) return
        setConfirmOpen(false)
        setIsPushing(true)
        try {
            const res = await cuPushLeads({
                filters,
                lead_list_id: leadListId,
                campaign_id: campaignId,
                duplicate_option: dupOption,
                clear_existing: clearExisting,
            })
            const d = res?.data as PushJob | undefined
            if (res?.success && d) {
                setJob(d)
                // "Clear & add fresh" is one-shot: left on, the next batch would wipe this one.
                setClearExisting(false)
                showToast(`${d.total.toLocaleString()} leads queued - uploading in the background`, 'success')
            } else {
                showToast(res?.message || 'Failed to queue the upload', 'error')
            }
        } catch (e: any) {
            showToast(e?.message || 'Could not reach the campaign upload service', 'error')
        } finally {
            setIsPushing(false)
        }
    }

    const handlePushClick = () => {
        if (leadListId == null) return
        if (isLargePush) { setConfirmOpen(true); return }
        doPush()
    }

    const doExport = async () => {
        if (!canExport) return
        setIsExporting(true)
        try {
            const res = await cuExportLeads(filters)
            const d = res?.data as { downloadUrl?: string; fileName?: string; recordCount?: number } | undefined
            if (res?.success && d?.downloadUrl) {
                downloadFile(d.downloadUrl, d.fileName)
                showToast(`Exported ${(d.recordCount ?? count ?? 0).toLocaleString()} lead(s)`, 'success')
            } else {
                showToast(res?.message || 'Failed to export leads', 'error')
            }
        } catch (e: any) {
            showToast(e?.message || 'Could not export leads', 'error')
        } finally {
            setIsExporting(false)
        }
    }

    const selectedList = leadLists.find(l => l.list_id === leadListId)
    const selectedCampaign = campaigns.find(c => c.campaign_id === campaignId)

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="px-6 py-4 bg-white border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg text-white" style={{ backgroundColor: BRAND }}>
                            <PhoneOutgoing className="w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="text-base font-semibold text-gray-900">Dialer Campaign Upload</h1>
                            <p className="text-xs text-gray-500">Filter leads and push them into a Tata Tele dialer campaign list</p>
                        </div>
                    </div>
                    <button
                        onClick={resetAll}
                        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
                    >
                        <RefreshCw size={13} /> Reset filters
                    </button>
                </div>
            </div>

            {/* Body: filters | destination */}
            <div className="flex flex-1 overflow-hidden">
                {/* ── Left: Filters ── */}
                <aside className="w-80 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
                    <div className="p-5 space-y-6">
                        <div className="flex items-center gap-2 text-gray-700">
                            <SlidersHorizontal className="w-4 h-4" />
                            <span className="text-sm font-semibold">Filters</span>
                        </div>

                        {/* Bucket */}
                        <div className="space-y-2">
                            <p className={sectionLabel}>Bucket</p>
                            <select value={filters.bucket ?? ''}
                                onChange={e => setF('bucket', (e.target.value || null) as CampaignFilters['bucket'])}
                                className={inputCls} style={{ ['--tw-ring-color' as any]: BRAND }}>
                                <option value="">All Buckets</option>
                                {BUCKET_OPTIONS.map(b => (
                                    <option key={b.value} value={b.value}>{b.label}</option>
                                ))}
                            </select>
                            {filters.bucket && AGEING_HINTS[filters.bucket] && (
                                <p className="text-[10px] text-gray-400">{AGEING_HINTS[filters.bucket]}</p>
                            )}
                        </div>

                        {/* EDD / NDR are filtered by ageing in days rather than a calendar
                            date: campaigns are described as "dispatched 5-15 days ago" or
                            "overdue by 2+ days", never as an absolute date window. */}
                        {isAgeingBucket && (
                            <div className="space-y-3">
                                <p className={sectionLabel}>Ageing</p>
                                <DayRange
                                    label="Dispatch Ageing (days)"
                                    hint="Days since the parcel was picked up."
                                    min={filters.dispatch_ageing_min} max={filters.dispatch_ageing_max}
                                    onChange={(b, v) => setF(b === 'min' ? 'dispatch_ageing_min' : 'dispatch_ageing_max', v)}
                                />
                                <DayRange
                                    label="EDD Ageing — days overdue"
                                    hint="Days past the promised delivery date. 0 = due today."
                                    min={filters.days_overdue_min} max={filters.days_overdue_max}
                                    onChange={(b, v) => setF(b === 'min' ? 'days_overdue_min' : 'days_overdue_max', v)}
                                />
                                <p className="text-[10px] text-gray-400">
                                    Orders with no dispatch scan (or no promised date) drop out once the
                                    matching range is set.
                                </p>
                            </div>
                        )}

                        {/* Bucket-specific date (hidden until a bucket is chosen) */}
                        {filters.bucket && !isAgeingBucket && (
                            <div className="space-y-3">
                                <p className={sectionLabel}>{DATE_LABELS[filters.bucket]}</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">From</label>
                                        <input type="date" value={filters.date_from ?? ''}
                                            onChange={e => setF('date_from', e.target.value || null)} className={smallInputCls} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">To</label>
                                        <input type="date" value={filters.date_to ?? ''}
                                            onChange={e => setF('date_to', e.target.value || null)} className={smallInputCls} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Disposition — per-level multi-select */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className={sectionLabel}>Disposition</p>
                                {(filters.l1.length + filters.l2.length + filters.l3.length) > 0 && (
                                    <button onClick={() => setFilters(prev => ({ ...prev, l1: [], l2: [], l3: [] }))}
                                        className="text-[11px] text-gray-400 hover:text-gray-600">Clear</button>
                                )}
                            </div>
                            <DispLevel label="Lead Status L1" options={tree?.l1Options || []} selected={filters.l1}
                                onToggle={toggleL1} onSelectAll={bulkL1} onClear={() => setFilters(prev => ({ ...prev, l1: [], l2: [], l3: [] }))}
                                disabled={treeLoading || !tree}
                                empty={treeLoading ? 'Loading…' : 'No L1 options'} />
                            <DispLevel label="Lead Status L2" options={l2Options} selected={filters.l2}
                                onToggle={toggleL2} onSelectAll={bulkL2} onClear={() => setFilters(prev => ({ ...prev, l2: [], l3: [] }))}
                                disabled={filters.l1.length === 0}
                                empty={filters.l1.length === 0 ? 'Select L1 first' : 'No L2 options'} />
                            <DispLevel label="Lead Status L3" options={l3Options} selected={filters.l3}
                                onToggle={toggleL3} onSelectAll={bulkL3} onClear={() => setF('l3', [])}
                                disabled={filters.l2.length === 0}
                                empty={filters.l2.length === 0 ? 'Select L2 first' : 'No L3 options'} />
                        </div>

                        {/* Products — category tree */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <p className={sectionLabel}>Products</p>
                                {filters.product_ids.length > 0 && (
                                    <button onClick={() => setF('product_ids', [])} className="text-[11px] text-gray-400 hover:text-gray-600">Clear ({filters.product_ids.length})</button>
                                )}
                            </div>
                            <input value={productQuery} onChange={e => setProductQuery(e.target.value)}
                                placeholder="Search products…" className={smallInputCls} />
                            <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto divide-y divide-gray-100">
                                {productTree.length === 0 ? (
                                    <div className="px-3 py-4 text-xs text-gray-400 flex items-center gap-2 justify-center">
                                        <Package size={13} /> No categories loaded
                                    </div>
                                ) : filteredCats.length === 0 ? (
                                    <div className="px-3 py-4 text-xs text-gray-400 text-center">No products match</div>
                                ) : filteredCats.map(cat => {
                                    const ids = cat.products.map(p => p.product_id)
                                    const selCount = ids.filter(id => filters.product_ids.includes(id)).length
                                    const allOn = selCount > 0 && selCount === ids.length
                                    const open = expandedCats.has(cat.cat_id) || productQuery.trim().length > 0
                                    return (
                                        <div key={cat.cat_id}>
                                            <div className="flex items-center gap-2 px-2 py-2 bg-gray-50/60">
                                                <button onClick={() => toggleCat(cat.cat_id)} className="text-gray-400 hover:text-gray-600 shrink-0">
                                                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                </button>
                                                <button onClick={() => toggleCatProducts(cat)}
                                                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${allOn ? 'border-transparent text-white' : selCount > 0 ? 'border-transparent text-white opacity-60' : 'border-gray-300'}`}
                                                    style={selCount > 0 ? { backgroundColor: BRAND } : undefined} title="Select all in category">
                                                    {selCount > 0 && <Check size={11} />}
                                                </button>
                                                <button onClick={() => toggleCat(cat.cat_id)} className="flex-1 flex items-center justify-between min-w-0 text-left">
                                                    <span className="text-xs font-medium text-gray-800 truncate flex items-center gap-1.5">
                                                        {cat.colour && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.colour }} />}
                                                        {cat.cat}
                                                    </span>
                                                    <span className="text-[10px] text-gray-400 ml-2 shrink-0">{selCount ? `${selCount}/` : ''}{ids.length}</span>
                                                </button>
                                            </div>
                                            {open && cat.products.map(p => {
                                                const active = filters.product_ids.includes(p.product_id)
                                                return (
                                                    <button key={p.product_id} onClick={() => toggleProduct(p.product_id)}
                                                        className="w-full flex items-center gap-2 pl-9 pr-3 py-1.5 text-left hover:bg-gray-50">
                                                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${active ? 'border-transparent text-white' : 'border-gray-300'}`}
                                                            style={active ? { backgroundColor: BRAND } : undefined}>
                                                            {active && <Check size={11} />}
                                                        </span>
                                                        <span className="text-xs text-gray-700 truncate">{p.name}</span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    )
                                })}
                            </div>
                            <p className="text-[10px] text-gray-400">Product match auto-scopes by bucket (cart items for Abandoned Cart, order line items for Reorder, RTO, EDD and NDR).</p>
                        </div>
                    </div>
                </aside>

                {/* ── Right: Destination & Push ── */}
                <main className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-2xl mx-auto space-y-5">
                        {tataPreview && (
                            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
                                <Info size={15} className="mt-0.5 shrink-0" />
                                <span>Showing sample Tata Tele data — the live campaign service isn't connected yet, so pushing is disabled. The filter UI is fully functional.</span>
                            </div>
                        )}

                        {/* Match count */}
                        <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-11 h-11 rounded-xl bg-gray-50 flex items-center justify-center">
                                        <Users className="text-gray-500" size={20} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Leads matching this filter</p>
                                        <div className="text-2xl font-bold text-gray-900 leading-tight">
                                            {countLoading
                                                ? <Loader2 className="animate-spin text-gray-400" size={22} />
                                                : countUnavailable ? <span className="text-base font-medium text-gray-400">—</span>
                                                : (count ?? 0).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {isLargePush && (
                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                                            <AlertTriangle size={12} /> Large push
                                        </span>
                                    )}
                                    <button onClick={doExport} disabled={!canExport}
                                        title={exportTooLarge ? `Export is limited to ${EXPORT_MAX_ROWS.toLocaleString()} leads — narrow the filters first` : undefined}
                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-full px-3 py-1.5 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                                        {isExporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                            {countUnavailable && (
                                <p className="text-[11px] text-gray-400 mt-2">Count unavailable until the backend endpoint is connected.</p>
                            )}
                            {exportTooLarge && (
                                <p className="text-[11px] text-amber-600 mt-2">
                                    Export is limited to {EXPORT_MAX_ROWS.toLocaleString()} leads — narrow the filters to export this set.
                                </p>
                            )}
                        </div>

                        {/* Result or destination form */}
                        {jobActive && job ? (
                            <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-6 flex flex-col items-center text-center">
                                <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                                    <Loader2 className="text-amber-600 animate-spin" size={28} />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {job.status === 'queued' ? 'Upload queued' : 'Uploading to Tata Tele'}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {job.total.toLocaleString()} leads - {selectedList?.name || `list ${job.job_id.slice(0, 8)}`}
                                </p>
                                <p className="text-xs text-gray-500 mt-3 max-w-sm">
                                    {job.status === 'queued'
                                        ? (job.ahead > 0
                                            ? `Waiting for ${job.ahead} other upload${job.ahead > 1 ? 's' : ''} to finish. It will start on its own.`
                                            : 'Starting shortly.')
                                        : (job.note || 'Working...')}
                                </p>
                                {job.batches_total > 0 && (
                                    <div className="w-full max-w-sm mt-5">
                                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                            <div className="h-full rounded-full transition-all"
                                                style={{ width: `${Math.round((job.batches_done / job.batches_total) * 100)}%`, backgroundColor: BRAND }} />
                                        </div>
                                        <div className="flex justify-between text-[11px] text-gray-500 mt-1.5">
                                            <span>Batch {Math.min(job.batches_done + (job.status === 'running' ? 1 : 0), job.batches_total)} of {job.batches_total}</span>
                                            <span>{job.pushed.toLocaleString()} sent{job.failed > 0 ? ` - ${job.failed.toLocaleString()} failed` : ''}</span>
                                        </div>
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-400 mt-5 max-w-sm">
                                    Tata Tele accepts leads in small batches with a pause between them, so a large upload takes a few minutes.
                                    You can leave this page - the upload keeps running.
                                </p>
                            </div>
                        ) : result ? (
                            <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-6 flex flex-col items-center text-center">
                                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-4">
                                    <CheckCircle2 className="text-green-600" size={30} />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Upload complete</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedList?.name}{selectedCampaign ? ` · ${selectedCampaign.name}` : ''}
                                </p>
                                {typeof result.cleared === 'number' && result.cleared > 0 && (
                                    <p className="text-xs text-amber-600 mt-1">Cleared {result.cleared.toLocaleString()} existing lead(s) first</p>
                                )}
                                <div className="grid grid-cols-3 gap-3 w-full max-w-sm mt-6">
                                    {[
                                        { label: 'Pushed', value: result.pushed, tone: 'text-green-600' },
                                        { label: 'Skipped', value: result.skipped, tone: 'text-amber-600' },
                                        { label: 'Failed', value: result.failed, tone: 'text-red-600' },
                                    ].map(s => (
                                        <div key={s.label} className="rounded-xl border border-gray-100 bg-gray-50 py-3">
                                            <div className={`text-2xl font-bold ${s.tone}`}>{s.value}</div>
                                            <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-0.5">{s.label}</div>
                                        </div>
                                    ))}
                                </div>
                                {result.failed > 0 && (
                                    <div className="w-full max-w-sm mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-left">
                                        <p className="text-xs font-medium text-red-700">{result.message}</p>
                                        {result.errors && result.errors.length > 0 && (
                                            <ul className="mt-1.5 space-y-1 list-disc pl-4">
                                                {result.errors.map((e, i) => (
                                                    <li key={i} className="text-[11px] text-red-600 break-words">{e}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-400 mt-4">Next batch will append to this list.</p>
                                <button onClick={() => setResult(null)}
                                    className="mt-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium hover:opacity-90"
                                    style={{ backgroundColor: BRAND }}>
                                    Push another batch
                                </button>
                            </div>
                        ) : (
                            <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-5 space-y-5">
                                <p className="text-sm font-semibold text-gray-900">Destination</p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Tata Lead List</label>
                                            <button type="button" onClick={() => setCreateOpen(true)} disabled={tataPreview}
                                                className="inline-flex items-center gap-1 text-[11px] font-medium hover:opacity-80 disabled:opacity-40"
                                                style={{ color: BRAND }}>
                                                <Plus size={12} /> New list
                                            </button>
                                        </div>
                                        <select value={leadListId ?? ''} onChange={e => setLeadListId(e.target.value ? Number(e.target.value) : null)}
                                            className={inputCls} style={{ ['--tw-ring-color' as any]: BRAND }}>
                                            <option value="">Select a lead list…</option>
                                            {leadLists.map(l => (
                                                <option key={l.list_id} value={l.list_id} disabled={l.list_id === 0}>
                                                    {l.name}{typeof l.lead_count === 'number' ? ` (${l.lead_count})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Campaign <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
                                        <select value={campaignId ?? ''} onChange={e => setCampaignId(e.target.value ? Number(e.target.value) : null)}
                                            className={inputCls} style={{ ['--tw-ring-color' as any]: BRAND }}>
                                            <option value="">No campaign</option>
                                            {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Clear vs append */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Upload mode</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button onClick={() => setClearExisting(false)}
                                            className={`rounded-xl border px-3 py-2.5 text-left transition-all ${!clearExisting ? 'border-transparent ring-2 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}
                                            style={!clearExisting ? { ['--tw-ring-color' as any]: BRAND } : undefined}>
                                            <span className="block text-sm font-medium text-gray-900">Append</span>
                                            <span className="block text-[10px] text-gray-500">Add to existing leads</span>
                                        </button>
                                        <button onClick={() => setClearExisting(true)}
                                            className={`rounded-xl border px-3 py-2.5 text-left transition-all ${clearExisting ? 'border-transparent ring-2 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                                            style={clearExisting ? { ['--tw-ring-color' as any]: '#d97706' } : undefined}>
                                            <span className="block text-sm font-medium text-gray-900">Clear &amp; add fresh</span>
                                            <span className="block text-[10px] text-gray-500">Remove all, then add</span>
                                        </button>
                                    </div>
                                    {clearExisting && (
                                        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700">
                                            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                            All leads currently in the selected list will be permanently removed before the new set is added.
                                        </p>
                                    )}
                                </div>

                                {!clearExisting && (
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">If a lead already exists in the list</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {DUP_OPTIONS.map(opt => {
                                                const active = dupOption === opt.value
                                                return (
                                                    <button key={opt.value} onClick={() => setDupOption(opt.value)}
                                                        className={`rounded-xl border px-2 py-2.5 text-center transition-all ${active ? 'border-transparent ring-2 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}
                                                        style={active ? { ['--tw-ring-color' as any]: BRAND } : undefined}>
                                                        <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                                                        <span className="block text-[10px] text-gray-500">{opt.hint}</span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}

                                <button onClick={handlePushClick} disabled={!canPush}
                                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: BRAND }}>
                                    {isPushing
                                        ? <><Loader2 size={16} className="animate-spin" /> Pushing…</>
                                        : <><PhoneOutgoing size={16} /> Push {typeof count === 'number' ? `${count.toLocaleString()} ` : ''}leads to campaign <ChevronRight size={16} /></>}
                                </button>
                                {leadListId == null && !tataPreview && (
                                    <p className="text-[11px] text-center text-gray-400">Select a Tata lead list to enable push.</p>
                                )}
                            </div>
                        )}
                    </div>
                </main>
            </div>

            {/* Large-push confirm */}
            {confirmOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setConfirmOpen(false)} />
                    <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                                <AlertTriangle className="text-amber-600" size={20} />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900">Confirm large push</h3>
                        </div>
                        <p className="text-sm text-gray-600">
                            You're about to push <strong>{count?.toLocaleString()}</strong> leads into{' '}
                            <strong>{selectedList?.name}</strong>. This can take a while and will dial a lot of customers. Continue?
                        </p>
                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setConfirmOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
                            <button onClick={doPush}
                                className="px-5 py-2.5 rounded-xl text-white text-sm font-medium hover:opacity-90"
                                style={{ backgroundColor: BRAND }}>Yes, push {count?.toLocaleString()} leads</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create lead list */}
            {createOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => !creating && setCreateOpen(false)} />
                    <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">New Tata lead list</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Created with the standard 12-column layout for this screen.</p>
                            </div>
                            <button onClick={() => !creating && setCreateOpen(false)} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X size={20} /></button>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">List name</label>
                                <input value={newName} maxLength={25} onChange={e => setNewName(e.target.value)}
                                    placeholder="e.g. July ABC High Priority"
                                    className={`${inputCls} ${newName && !nameValid ? 'border-red-300' : ''}`}
                                    style={{ ['--tw-ring-color' as any]: BRAND }} />
                                <div className="flex items-center justify-between mt-1">
                                    <span className={`text-[11px] ${newName && !nameValid ? 'text-red-500' : 'text-gray-400'}`}>
                                        Letters, numbers & spaces only
                                    </span>
                                    <span className="text-[11px] text-gray-400">{newName.trim().length}/25</span>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Description <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
                                <input value={newDesc} onChange={e => setNewDesc(e.target.value)}
                                    placeholder="What this list is for" className={inputCls} style={{ ['--tw-ring-color' as any]: BRAND }} />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                            <button onClick={() => setCreateOpen(false)} disabled={creating}
                                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-40">Cancel</button>
                            <button onClick={handleCreateList} disabled={!nameValid || creating}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                                style={{ backgroundColor: BRAND }}>
                                {creating ? <><Loader2 size={16} className="animate-spin" /> Creating…</> : <><Plus size={16} /> Create list</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CampaignUpload
